/**
 * Accept Invite Page
 * Allows accountants to accept report collaboration invites via token
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import axios from '@/lib/http';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { accountant, getAuthHeader } = useAccountantAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<any>(null);

  useEffect(() => {
    if (!accountant) {
      // Store the invite token and redirect to login
      sessionStorage.setItem('pendingInviteToken', token || '');
      navigate(`/accountant/login?returnUrl=${encodeURIComponent(`/accountant/accept-invite/${token}`)}`);
      return;
    }

    // Accountant is logged in, accept the invite
    acceptInvite();
  }, [accountant, token]);

  const acceptInvite = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/invites/accept`,
        { invite_token: token },
        { headers: getAuthHeader() }
      );

      setInviteDetails(response.data);
      setSuccess(true);
      
      // Auto-redirect to the report after 2 seconds
      setTimeout(() => {
        navigate(`/accountant/reports/${response.data.report_id}`);
      }, 2000);
      
    } catch (err: any) {
      console.error('Error accepting invite:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to accept invite. The link may be invalid or expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div
          className="rounded-2xl p-8 max-w-md w-full text-center"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        >
          <div
            className="animate-spin rounded-full h-16 w-16 mx-auto mb-4"
            style={{ borderBottom: '4px solid var(--color-brand)' }}
          ></div>
          <h2 className="cs-h2 mb-2">
            Processing Invite...
          </h2>
          <p className="cs-body">
            Please wait while we activate your access
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div
          className="rounded-2xl p-8 max-w-md w-full"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        >
          <div className="text-center mb-6">
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
            >
              <svg className="w-10 h-10" style={{ color: 'var(--color-needs-human-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="cs-h2 mb-2">
              Unable to Accept Invite
            </h2>
            <p className="cs-body mb-6" style={{ color: 'var(--color-text-muted)' }}>{error}</p>
          </div>

          <div className="space-y-3">
            <Link
              to="/accountant/reports"
              className="block w-full px-6 py-3 rounded-lg transition text-center cs-label"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              Go to My Reports
            </Link>
            <Link
              to="/accountant/login"
              className="block w-full px-6 py-3 rounded-lg transition text-center cs-label"
              style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-elevated)'; }}
            >
              Login with Different Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success && inviteDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div
          className="rounded-2xl p-8 max-w-md w-full"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        >
          <div className="text-center mb-6">
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 animate-bounce"
              style={{ background: 'transparent' }}
            >
              <svg className="w-10 h-10" style={{ color: 'var(--color-verified)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="cs-h2 mb-2">
              Invite Accepted!
            </h2>
            <p className="cs-body mb-4" style={{ color: 'var(--color-text-muted)' }}>
              You now have access to this report
            </p>

            <div
              className="rounded-lg p-4 text-left"
              style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}
            >
              <p className="cs-meta mb-1">Report Shared By:</p>
              <p className="cs-body mb-3" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{inviteDetails.shared_by_name}</p>

              <p className="cs-meta mb-1">Your Permissions:</p>
              <div className="flex flex-wrap gap-2">
                {inviteDetails.permissions.map((perm: string) => (
                  <span
                    key={perm}
                    className="cs-chip px-2 py-1 rounded-full"
                    style={{ background: 'transparent', color: 'var(--color-verified)' }}
                  >
                    {perm}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <p className="cs-meta text-center mb-4">
            Redirecting you to the report...
          </p>

          <Link
            to={`/accountant/reports/${inviteDetails.report_id}`}
            className="block w-full px-6 py-3 rounded-lg transition text-center cs-label"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
          >
            View Report Now
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
